import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// ALDH2 *1/*2 + ADH1B fast + GSTM1 null — the liver enzymes that kept rising
// despite drinking less than his colleagues.
// The aha: "safe drinking" guidelines were built on populations with intact ALDH2.
// For ~30–40% of East Asian people, those limits are calibrated for someone else's liver.

export const alcoholKenji: Scenario = {
  slug: 'alcohol-kenji',
  series: 'Detox & Liver',
  title: "He drank less than his friends. His liver numbers still rose.",
  socialHook:
    "Kenji, 44: 6–8 drinks a week, careful tracking, and three years of high ALT and GGT. The missing clue was that the usual alcohol guidelines were built for a different liver.",

  meta: {
    description:
      "A fictional case study: Kenji drinks 6–8 drinks per week, but his ALDH2 *1/*2, fast ADH1B, and GSTM1 null results explain why that modest intake can create a much higher acetaldehyde burden for his liver.",
  },

  persona: {
    name: 'Kenji',
    age: 44,
    sex: 'Male',
    ancestry: 'Japanese-British',
    occupation: 'Architect',
    chiefConcern:
      "Kenji's ALT and GGT have been high at routine blood tests for three years. He already drinks only 6–8 drinks a week and has cut back twice, but the numbers keep creeping up.",
    familyHistory:
      "Father: liver cirrhosis — attributed to heavy drinking, though father drank similarly to the Japanese average. Paternal uncle: esophageal cancer.",
  },

  clinicalPicture: {
    symptoms: [
      "No symptoms of acute liver disease — no jaundice, no abdominal pain, no fatigue beyond baseline",
      "Mild flushing and rapid heartbeat after alcohol — present since his twenties, normalized as 'just how I react'",
      "Health anxiety about the persistent abnormal bloods despite compliant behavior",
    ],
    labs: [
      { name: 'ALT', value: '54 U/L', refRange: '<40 U/L', flag: 'high' },
      { name: 'GGT', value: '72 U/L', refRange: '<50 U/L', flag: 'high' },
      { name: 'Liver ultrasound', value: 'Mild fatty change (NAFLD pattern)', refRange: 'Normal appearance', flag: 'high' },
    ],
    medications: ['No prescription medications'],
    supplements: ['None currently'],
    lifestyle: [
      'Alcohol: 6–8 drinks/week — wine with dinner, occasional beer. Tracked carefully.',
      'Diet: varied, no specific restrictions',
      'Exercise: walks to work, occasional cycling',
      'Non-smoker',
      'Sleep: 7–8 hours, good quality',
    ],
  },

  genetics: [
    {
      gene: 'ALDH2',
      variant: 'rs671 (Glu504Lys, heterozygous *1/*2)',
      rsid: 'rs671',
      genotype: 'ALDH2 *1/*2 — heterozygous deficiency',
      plainMeaning:
        "Kenji clears a toxic alcohol by-product much more slowly than most people, so modest drinking can leave a bigger liver burden.",
      technicalDetail:
        "ALDH2 is the enzyme that converts acetaldehyde — the toxic intermediate produced when alcohol is metabolized — into harmless acetate. The *2 variant reduces ALDH2 activity by approximately 80% in heterozygotes. Acetaldehyde is classified as a Group 1 carcinogen by the IARC and is a direct hepatotoxin. Kenji's 6–8 drinks per week generates an acetaldehyde liver burden equivalent to roughly 28–40 drinks/week for an ALDH2-normal person. Standard guidelines capping moderate drinking at ~14 drinks/week were derived largely from populations without this variant and do not apply cleanly to him.",
    },
    {
      gene: 'ADH1B',
      variant: 'rs1229984 (Arg48His, fast metabolizer allele)',
      rsid: 'rs1229984',
      genotype: 'ADH1B fast metabolizer — common in East Asian ancestry',
      plainMeaning:
        "Kenji turns alcohol into acetaldehyde quickly, then clears acetaldehyde slowly. That combination makes the spike higher and longer.",
      technicalDetail:
        "ADH1B converts alcohol into acetaldehyde in the first metabolic step. The fast-metabolizer allele accelerates that conversion, producing an acetaldehyde spike within minutes of a drink. Combined with slow ALDH2 clearance, the spike is both higher and more prolonged. This ADH1B-fast + ALDH2-deficient combination is the biochemical mechanism behind the flushing, rapid heartbeat, and nausea common in East Asian populations after small amounts of alcohol — the so-called 'Asian flush'. For Kenji, that reaction is a real-time signal of acetaldehyde accumulation, not a quirk.",
    },
    {
      gene: 'GSTM1',
      variant: 'Deletion (null genotype)',
      rsid: undefined,
      genotype: 'GSTM1 null — complete deletion of one detoxification pathway',
      plainMeaning:
        "One backup detox route is missing, leaving fewer ways to handle the oxidative stress that follows acetaldehyde exposure.",
      technicalDetail:
        "GSTM1 is a phase II detoxification enzyme responsible for clearing oxidative stress products generated during acetaldehyde metabolism. The null genotype means this pathway does not exist — roughly 50% of East Asian individuals carry this deletion. For Kenji, the picture compounds: elevated acetaldehyde exposure from every drink, reduced ALDH2 clearance capacity, and no GSTM1 backup for the resulting oxidative damage. His father's cirrhosis at a 'moderate' intake, and the NAFLD finding on Kenji's ultrasound despite a healthy weight and no dietary excess, are both consistent with this three-variant combination — acetaldehyde and oxidative stress each drive hepatic fat accumulation independently of diet or calories.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "Three years of being told to drink less",
      body: "Kenji tracks his alcohol carefully: six to eight drinks a week, mostly wine with dinner and the occasional beer. He watches colleagues drink more at work events and still report normal liver tests. His annual labs are different every time: ALT high, GGT high, and the same advice to cut back. He has cut back twice. The numbers dip a little, then climb again. He starts to wonder whether his family doctor thinks he is hiding something.",
    },
    {
      type: 'narrative',
      heading: "What 'safe drinking' guidelines can miss",
      body: "Alcohol guidelines describe population averages. They assume the liver turns alcohol into acetaldehyde, then clears acetaldehyde at roughly normal speed. Kenji's mystery lives in that second step. If the clean-up step barely works, a drink is not the same exposure it is for someone with typical ALDH2 function. For many people with East Asian ancestry, standard limits can be calibrated for someone else's liver.",
    },
    {
      type: 'reveal',
      heading: "His 6–8 drinks were landing like far more",
      body: "The issue was not the number of drinks on the page. It was the toxin left behind after each drink. Kenji's ADH1B result means alcohol becomes acetaldehyde quickly, and his ALDH2 result means acetaldehyde clears slowly. That turns a modest weekly intake into a much larger liver exposure than the guidelines suggest. His persistently elevated enzymes now look less mysterious, and his father's cirrhosis at a 'moderate' intake may fit the same inherited pattern.",
    },
    {
      type: 'reveal',
      heading: "The flushing was a clue",
      body: "The flushing and racing heart Kenji has had since his twenties were not just a personal quirk. They were a real-time sign that acetaldehyde was building up. His GSTM1 null result adds one more layer by removing a backup route for oxidative stress. Even the mild fatty change on ultrasound fits the story: acetaldehyde and oxidative stress can strain the liver independent of calories or body weight.",
    },
    {
      type: 'action',
      heading: "Five things to discuss with his clinician",
      items: [
        "Consider a 3-month alcohol-free trial with his family doctor to establish a clean baseline for ALT, GGT, and the fatty liver finding.",
        "Share the ALDH2 result directly. This reframes the issue from compliance to alcohol metabolism and supports a hepatology referral given the abnormal ultrasound and persistent enzyme elevation.",
        "Use food to support other detox pathways. Daily cruciferous vegetables such as broccoli, brussels sprouts, kale, and cabbage may help activate routes that partially compensate for missing GSTM1.",
        "Encourage first-degree relatives to ask about ALDH2 testing if they drink. The variant is inherited, so standard guidelines may also mislead them.",
        "Ask about esophageal cancer surveillance. ALDH2 *2 plus regular alcohol exposure is a known risk factor, and his paternal uncle's esophageal cancer makes this worth discussing.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'riskdial' as const,
      heading: "What 6 drinks means for Kenji's liver",
      caption:
        "For Kenji's liver, 6–8 drinks can create the acetaldehyde burden of 28–40 drinks per week for someone with normal ALDH2. The dial changes when the exposure changes.",
      baselineLabel: "6–8 drinks/week with ALDH2 *1/*2 + ADH1B fast + GSTM1 null",
      baseline: 70,
      levers: [
        {
          label: "Reduce to <4 drinks/week",
          delta: -22,
          description: "Each drink below current intake directly reduces acetaldehyde accumulation. For ALDH2 *1/*2 carriers, the dose-response is steeper than average.",
        },
        {
          label: "3-month alcohol-free trial",
          delta: -30,
          description: "Removes acetaldehyde exposure long enough to see whether ALT, GGT, and the NAFLD finding improve. Establishes a clean baseline.",
        },
        {
          label: "Cruciferous vegetables daily (NRF2/GSTM1 compensation)",
          delta: -8,
          description: "Sulforaphane and indole-3-carbinol from broccoli, brussels sprouts, and kale upregulate NRF2-driven phase II detox pathways, partially compensating for absent GSTM1 function and reducing oxidative stress burden in the liver.",
        },
        {
          label: "Avoid alcohol + paracetamol combination",
          delta: -5,
          description: "Acetaminophen/paracetamol at standard doses adds liver metabolic load through the CYP2E1 pathway, which overlaps with alcohol-induced oxidative stress.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'alcohol-kenji',
    label: 'Explore your alcohol metabolism genetics in Coach',
  },
}
